/**
 * What the offline repositories, outbox and sync engine need from the session (Phase 16C).
 * Production binds it to OfflineSession; tests pass a memory-backed store.
 */
import type { SecureStore } from './secureStore';

export interface OfflineIdentityContext {
  userId: string;
  tenantId: string;
  deviceId: string;
}

export interface OfflineRuntime {
  /** The unlocked store; throws when locked. */
  store(): SecureStore;
  /** tenantId / userId from the token claims captured at enrollment, and this device's id. */
  identity(): OfflineIdentityContext;
  now(): number;
}

export class OfflineLockedError extends Error {
  constructor() {
    super('Offline store is locked');
    this.name = 'OfflineLockedError';
  }
}

export interface SessionLike {
  getStore(): SecureStore | null;
  getProfile(): { user: { id: string; tenantId: string }; deviceId: string } | null;
}

export function sessionRuntime(session: SessionLike, now: () => number = Date.now): OfflineRuntime {
  return {
    store() {
      const store = session.getStore();
      if (!store) throw new OfflineLockedError();
      return store;
    },
    identity() {
      const profile = session.getProfile();
      if (!profile) throw new OfflineLockedError();
      return { userId: profile.user.id, tenantId: profile.user.tenantId, deviceId: profile.deviceId };
    },
    now,
  };
}
